import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useIdioma } from "../../i18n/IdiomaProvider";
import { estiloAvatarCadastro, iniciaisAvatarCadastro } from "../tabela/AvatarCadastro";
import { normalizarTextoPtBr } from "../../utils/textoPtBr";
import { abrirCadastroRapidoSelect, ehMensagemCadastroRapidoSelect } from "../../utils/cadastroRapidoSelect";
import { obterAtributosSemSugestoesNativas, useBloqueioAteFoco } from "./assistenciaTexto";
import { Popover } from "./Popover";
import { inferirTamanhoCampoModal, type TamanhoCampoModal } from "./campoModal";
import { RotuloObrigatorio } from "./rotuloObrigatorio";
import { cadastroEstaAtivo } from "../../utils/cadastrosAtivos";
import styles from "./CampoSelectCustom.module.css";

export interface OpcaoSelect {
  valor: string;
  rotulo: string;
  /** Ícone semântico pequeno, exibido junto ao nome na lista e na seleção. */
  icone?: ReactNode;
  detalhe?: string;
  disabled?: boolean;
  cor?: string;
  imagemUrl?: string;
  avatarTexto?: string;
  estoqueQuantidade?: number;
  busca?: string;
  dadoBanco?: boolean;
  /** Metadados de cadastro usados para ocultar opções inativas globalmente. */
  status?: unknown;
  ativo?: unknown;
}

export interface AcaoSelectCustom {
  id: string;
  rotulo: string;
  onClick?: () => void;
  /** Exibe a ação como botão de contorno no canto superior direito do menu. */
  alinhamento?: "inicio" | "fim";
  cadastroRapido?: {
    rota: string;
    tipo: string;
  };
}

export interface CampoSelectCustomProps {
  rotulo?: string;
  valor: string;
  opcoes: OpcaoSelect[];
  onChange: (valor: string) => void;
  /** Entrega a opcao completa, inclusive a criada pelo cadastro rapido. */
  onChangeOpcao?: (opcao: OpcaoSelect) => void;
  /** Valida o registro do cadastro rápido antes de incluí-lo nas opções e selecioná-lo. */
  aceitarCadastroRapido?: (registro: Record<string, unknown> | undefined) => boolean;
  /** Valor visual selecionado sem incluí-lo na lista de opções. */
  opcaoSelecionada?: OpcaoSelect;
  acoesInicio?: AcaoSelectCustom[];
  placeholder?: string;
  erro?: string;
  disabled?: boolean;
  obrigatorio?: boolean;
  className?: string;
  iconeInicio?: ReactNode;
  placeholderBusca?: string;
  /** false: só clique para selecionar, sem campo de digitação/filtro (padrão: true). */
  pesquisavel?: boolean;
  tamanhoCompacto?: TamanhoCampoModal;
  /** Ação opcional de padrão persistido; só aparece quando habilitada pelo modal chamador. */
  preferenciaPadrao?: {
    valor: string;
    salvando?: boolean;
    onSalvar: (valor: string) => void;
  };
  /** Oculta o avatar/imagem no valor selecionado (controle fechado), útil quando a imagem já aparece em destaque fora do campo. Não afeta as opções da lista. */
  ocultarAvatarSelecionado?: boolean;
  /**
   * Avatar de pessoa: círculo com as iniciais (ou a foto recortada), igual ao
   * `AvatarCadastro` das listagens. Sem isso o selo é o quadrado arredondado
   * usado por produto/empresa.
   */
  avatarRedondo?: boolean;
  /**
   * Mesmas cores do `AvatarCadastro`, mas com o selo quadrado arredondado do
   * campo. Usado nos filtros de Kanban/Gantt, onde o círculo destoava.
   */
  avatarQuadrado?: boolean;
  /** Miniatura horizontal ampliada para imagens de produtos e moldes. */
  imagemRetangular?: boolean;
}

const IcoSeta = () => (
  <svg viewBox="0 0 18 18" aria-hidden="true">
    <path d="m4.5 6.75 4.5 4.5 4.5-4.5" />
  </svg>
);

const ATRIBUTOS_SEM_SUGESTOES = obterAtributosSemSugestoesNativas();

function normalizarParaBusca(valor: unknown): string {
  return normalizarTextoPtBr(valor).toLocaleLowerCase("pt-BR").trim();
}

/**
 * O selo do select tem 22px: um nome completo em `avatarTexto` estoura o
 * círculo e deforma a linha do campo. Reduz para no máximo duas letras, o mesmo
 * critério do avatar padrão dos cadastros (`AvatarCadastro`).
 */
function textoAvatarSelect(valor: string | undefined): string {
  const texto = String(valor ?? "").trim();
  if (!texto) return "";
  if (texto.length <= 2 && !/\s/.test(texto)) return texto.toLocaleUpperCase("pt-BR");
  return iniciaisAvatarCadastro(texto);
}

function opcaoPareceDadoBanco(opcao: OpcaoSelect | null | undefined): boolean {
  if (opcao?.dadoBanco) return true;
  const valor = String(opcao?.valor ?? "").trim();
  if (!valor) return false;
  return /^\d+$/.test(valor) || /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(valor);
}

/**
 * Select personalizado (NUNCA o <select> nativo). O dropdown abre via <Popover>
 * em portal, entao nao e cortado pelo modal nem abre por baixo do menu.
 *
 * Padrao para dados vindos de cadastro: manter a busca ativa, informar
 * `imagemUrl`/`avatarTexto`, marcar `dadoBanco` e oferecer o cadastro rapido
 * em `acoesInicio`. Para cliente e colaborador, use os componentes dedicados.
 */
export function CampoSelectCustom({
  rotulo,
  valor,
  opcoes,
  onChange,
  onChangeOpcao,
  aceitarCadastroRapido,
  opcaoSelecionada,
  acoesInicio = [],
  placeholder = "Selecione...",
  erro,
  disabled = false,
  obrigatorio = false,
  className,
  iconeInicio,
  placeholderBusca = "Digite para filtrar...",
  pesquisavel = true,
  tamanhoCompacto,
  ocultarAvatarSelecionado = false,
  avatarRedondo = false,
  avatarQuadrado = false,
  imagemRetangular = false,
  preferenciaPadrao,
}: CampoSelectCustomProps) {
  const { traduzir } = useIdioma();
  const ancoraRef = useRef<HTMLDivElement>(null);
  const buscaRef = useRef<HTMLInputElement>(null);
  const retornosPendentesRef = useRef<Set<string>>(new Set());
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const [opcoesCriadas, setOpcoesCriadas] = useState<OpcaoSelect[]>([]);
  const bloqueioAteFoco = useBloqueioAteFoco();
  const listboxId = useId();
  const identidadeBusca = `i9select${listboxId.replace(/[^a-z0-9]/gi, "")}`;
  const valorNormalizado = String(valor ?? "");
  const todasOpcoesComCriadas = useMemo(() => {
    const mapa = new Map<string, OpcaoSelect>();
    [...opcoesCriadas, ...opcoes].forEach((opcao) => {
      const chave = String(opcao.valor ?? "");
      if (!chave || mapa.has(chave)) return;
      mapa.set(chave, opcao);
    });
    return Array.from(mapa.values());
  }, [opcoes, opcoesCriadas]);
  const opcoesComCriadas = useMemo(
    () => todasOpcoesComCriadas.filter((opcao) => cadastroEstaAtivo(opcao)),
    [todasOpcoesComCriadas]
  );
  const selecionada = String(opcaoSelecionada?.valor ?? "") === valorNormalizado
    ? opcaoSelecionada
    : opcoesComCriadas.find((o) => String(o.valor ?? "") === valorNormalizado);
  const selecionadaInativa = todasOpcoesComCriadas.some(
    (opcao) => String(opcao.valor ?? "") === valorNormalizado && !cadastroEstaAtivo(opcao)
  );
  // Um valor ja salvo (ex.: ao editar um registro) pode nao estar presente nas
  // opcoes carregadas no momento (filtro por empresa/organizacao, opcoes ainda
  // carregando, etc.). Sem isso o campo parece vazio mesmo com valor selecionado,
  // como se fosse um cadastro novo. Sintetiza uma opcao para o valor salvo em vez
  // de cair no placeholder.
  const opcaoValorSalvo: OpcaoSelect | null = !selecionada && !selecionadaInativa && valorNormalizado
    ? { valor: valorNormalizado, rotulo: valorNormalizado }
    : null;
  const opcoesExibidas = opcaoValorSalvo ? [opcaoValorSalvo, ...opcoesComCriadas] : opcoesComCriadas;
  const selecionadaExibida = selecionada ?? opcaoValorSalvo ?? undefined;
  const exibirImagemSelecionadaExterna =
    !ocultarAvatarSelecionado && Boolean(selecionadaExibida?.imagemUrl);
  const rotuloNormalizado = rotulo ? traduzir(normalizarTextoPtBr(rotulo)) : undefined;
  const placeholderNormalizado = traduzir(normalizarTextoPtBr(placeholder));
  const placeholderBuscaNormalizado = traduzir(normalizarTextoPtBr(placeholderBusca));
  const erroNormalizado = erro ? traduzir(normalizarTextoPtBr(erro)) : undefined;
  const selecionadaEhDadoBanco = opcaoPareceDadoBanco(selecionadaExibida);
  // Quando nada esta selecionado e o campo e invalido, o erro substitui o
  // placeholder e aparece dentro do proprio controle (nao abaixo).
  const placeholderExibido = erroNormalizado && !selecionadaExibida ? erroNormalizado : placeholderNormalizado;
  const rotuloSelecionado = selecionadaExibida
    ? selecionadaEhDadoBanco
      ? normalizarTextoPtBr(selecionadaExibida.rotulo)
      : traduzir(normalizarTextoPtBr(selecionadaExibida.rotulo))
    : placeholderExibido;
  // Mantém a largura estável entre opções, limitada para não dominar a grade do formulário.
  const larguraTextoSelect = useMemo(() => {
    const textos = [rotuloNormalizado, placeholderNormalizado, selecionadaExibida?.rotulo, ...opcoesComCriadas.map((opcao) => opcao.rotulo)];
    const maiorTexto = textos.reduce((maior, texto) => Math.max(maior, String(texto ?? "").trim().length), 0);
    return `${Math.min(maiorTexto, 54)}ch`;
  }, [opcoesComCriadas, placeholderNormalizado, rotuloNormalizado, selecionadaExibida?.rotulo]);
  const tamanhoCampoModal = useMemo(
    () =>
      inferirTamanhoCampoModal(
        [
          rotuloNormalizado,
          selecionadaExibida?.rotulo,
          placeholderNormalizado,
          ...opcoesComCriadas.map((opcao) => opcao.rotulo),
        ],
        tamanhoCompacto
      ),
    [
      opcoesComCriadas,
      placeholderNormalizado,
      rotuloNormalizado,
      selecionadaExibida?.rotulo,
      tamanhoCompacto,
    ]
  );
  const buscaNormalizada = useMemo(() => normalizarParaBusca(busca), [busca]);
  const opcoesFiltradas = useMemo(() => {
    if (!buscaNormalizada) return opcoesExibidas;
    return opcoesExibidas.filter((opcao) => {
      const textoOpcao = normalizarParaBusca([
        opcao.rotulo,
        opcao.valor,
        opcao.busca,
        opcao.detalhe,
      ]
        .filter(Boolean)
        .join(" "));
      return textoOpcao.includes(buscaNormalizada);
    });
  }, [buscaNormalizada, opcoesExibidas]);

  useEffect(() => {
    if (!aberto) {
      setBusca("");
      return;
    }
    const frame = window.requestAnimationFrame(() => buscaRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [aberto]);

  useEffect(() => {
    function receberCadastroRapido(evento: MessageEvent) {
      if (evento.origin !== window.location.origin) return;
      if (!ehMensagemCadastroRapidoSelect(evento.data)) return;
      if (!retornosPendentesRef.current.has(evento.data.token)) return;

      retornosPendentesRef.current.delete(evento.data.token);
      const opcao = evento.data.opcao;
      const proximoValor = String(opcao.valor ?? "");
      if (!proximoValor) return;
      if (aceitarCadastroRapido && !aceitarCadastroRapido(evento.data.registro)) return;

      setOpcoesCriadas((atuais) => {
        const filtradas = atuais.filter((item) => String(item.valor ?? "") !== proximoValor);
        return [opcao, ...filtradas];
      });
      onChange(proximoValor);
      onChangeOpcao?.(opcao);
    }

    window.addEventListener("message", receberCadastroRapido);
    return () => window.removeEventListener("message", receberCadastroRapido);
  }, [onChange, onChangeOpcao, aceitarCadastroRapido]);

  return (
    <div
      className={[
        styles.campo,
        exibirImagemSelecionadaExterna ? styles.campoComImagemSelecionada : "",
        className,
      ].filter(Boolean).join(" ")}
      style={{ ["--largura-texto-select" as string]: larguraTextoSelect } as CSSProperties}
      data-campo-modal="true"
      data-avatar-redondo={avatarRedondo ? "true" : undefined}
      data-avatar-quadrado={avatarQuadrado ? "true" : undefined}
      data-imagem-retangular={imagemRetangular ? "true" : undefined}
      data-campo-tamanho={tamanhoCampoModal}
      data-campo-progresso={disabled && !valorNormalizado ? "false" : "true"}
      data-campo-preenchido={valorNormalizado ? "true" : "false"}
      data-campo-invalido={erroNormalizado ? "true" : "false"}
    >
      {rotuloNormalizado && (
        <label className={styles.rotulo}>
          <RotuloObrigatorio rotulo={rotuloNormalizado} obrigatorio={obrigatorio} />
        </label>
      )}
      <div className={styles.linhaControle}>
        {exibirImagemSelecionadaExterna && selecionadaExibida?.imagemUrl ? (
          <img
            className={styles.imagemSelecionadaExterna}
            src={selecionadaExibida.imagemUrl}
            alt=""
            aria-hidden="true"
          />
        ) : null}
        <div ref={ancoraRef} className={styles.ancora}>
        {pesquisavel && aberto && !disabled ? (
          <div
            className={`${styles.controle} ${erroNormalizado ? styles.invalido : ""} ${styles.aberto} ${styles.controleDigitavel}`}
            aria-haspopup={preferenciaPadrao ? "dialog" : "listbox"}
            aria-expanded={aberto}
            aria-required={obrigatorio || undefined}
            aria-invalid={erroNormalizado ? true : undefined}
          >
            <span className={styles.valorConteudo}>
              {iconeInicio ? <span className={styles.iconeInicio} aria-hidden="true">{iconeInicio}</span> : null}
              <input
                ref={buscaRef}
                type="search"
                name={identidadeBusca}
                value={busca}
                placeholder={placeholderBuscaNormalizado}
                className={styles.controleInput}
                onChange={(evento) => setBusca(evento.target.value)}
                onKeyDown={(evento) => {
                  if (evento.key === "Escape") {
                    evento.preventDefault();
                    setAberto(false);
                  }
                }}
                role="combobox"
                aria-haspopup={preferenciaPadrao ? "dialog" : undefined}
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-expanded={aberto}
                aria-invalid={erroNormalizado ? true : undefined}
                autoComplete={`section-${identidadeBusca} one-time-code`}
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
                data-form-type={ATRIBUTOS_SEM_SUGESTOES["data-form-type"]}
                data-lpignore={ATRIBUTOS_SEM_SUGESTOES["data-lpignore"]}
                data-1p-ignore={ATRIBUTOS_SEM_SUGESTOES["data-1p-ignore"]}
                data-bwignore={ATRIBUTOS_SEM_SUGESTOES["data-bwignore"]}
                readOnly={bloqueioAteFoco.readOnly}
                onFocus={bloqueioAteFoco.onFocus}
                onBlur={bloqueioAteFoco.onBlur}
              />
            </span>
            <button
              type="button"
              className={styles.setaBotao}
              onClick={() => setAberto(false)}
              aria-label={traduzir("Fechar opções")}
            >
              <span className={styles.seta} aria-hidden="true">
                <IcoSeta />
              </span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            disabled={disabled}
            className={`${styles.controle} ${erroNormalizado ? styles.invalido : ""}`}
            onClick={() => setAberto(true)}
            onKeyDown={(evento) => {
              if (disabled) return;
              if (evento.key === "ArrowDown" || evento.key === "Enter" || evento.key === " ") {
                evento.preventDefault();
                setAberto(true);
                return;
              }
              if (pesquisavel && evento.key.length === 1 && !evento.ctrlKey && !evento.metaKey && !evento.altKey) {
                setBusca(evento.key);
                setAberto(true);
              }
            }}
            aria-haspopup={preferenciaPadrao ? "dialog" : "listbox"}
            aria-expanded={aberto}
            aria-required={obrigatorio || undefined}
            aria-invalid={erroNormalizado ? true : undefined}
          >
            <span className={styles.valorConteudo}>
              {iconeInicio ? <span className={styles.iconeInicio} aria-hidden="true">{iconeInicio}</span> : null}
              {!ocultarAvatarSelecionado && selecionadaExibida?.icone ? (
                <span className={styles.iconeOpcao} aria-hidden="true">{selecionadaExibida.icone}</span>
              ) : ocultarAvatarSelecionado || selecionadaExibida?.imagemUrl ? null : selecionadaExibida?.avatarTexto ? (
                <span
                  className={styles.thumbFallback}
                  style={avatarRedondo || avatarQuadrado ? estiloAvatarCadastro(selecionadaExibida.avatarTexto) : undefined}
                  aria-hidden="true"
                >
                  {textoAvatarSelect(selecionadaExibida.avatarTexto)}
                </span>
              ) : selecionadaExibida?.cor ? (
                <span
                  className={styles.bolinhaCor}
                  style={{ ["--opcao-cor" as string]: selecionadaExibida.cor } as CSSProperties}
                  aria-hidden="true"
                />
              ) : null}
              <span
                className={
                  selecionadaExibida
                    ? styles.valor
                    : erroNormalizado
                      ? styles.placeholderErro
                      : styles.placeholder
                }
                data-i18n-db={selecionadaEhDadoBanco ? "true" : undefined}
              >
                {rotuloSelecionado}
              </span>
            </span>
            <span className={styles.seta} aria-hidden="true">
              <IcoSeta />
            </span>
          </button>
        )}
        </div>
      </div>

      <Popover ancora={ancoraRef} aberto={aberto} largura="conteudo" onFechar={() => setAberto(false)}>
        <div
          id={listboxId}
          className={styles.lista}
          role={preferenciaPadrao ? "dialog" : "listbox"}
          aria-label={preferenciaPadrao ? rotuloNormalizado || traduzir("Opções e padrão") : undefined}
          data-avatar-redondo={avatarRedondo ? "true" : undefined}
          data-avatar-quadrado={avatarQuadrado ? "true" : undefined}
          data-imagem-retangular={imagemRetangular ? "true" : undefined}
        >
          {acoesInicio.length ? (
            <div className={styles.acoesInicio}>
              {acoesInicio.map((acao) => (
                <button
                  key={acao.id}
                  type="button"
                  className={`${styles.opcao} ${styles.opcaoAcao} ${acao.alinhamento === "fim" ? styles.opcaoAcaoFim : ""}`}
                  onClick={() => {
                    if (acao.cadastroRapido) {
                      const retorno = abrirCadastroRapidoSelect(acao.cadastroRapido.rota, acao.cadastroRapido.tipo);
                      retornosPendentesRef.current.add(retorno.token);
                    } else {
                      acao.onClick?.();
                    }
                    setAberto(false);
                  }}
                >
                  <span className={styles.opcaoConteudo}>
                    <span>{traduzir(normalizarTextoPtBr(acao.rotulo))}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          {opcoesFiltradas.map((opcao) => {
            const opcaoEhDadoBanco = opcaoPareceDadoBanco(opcao);
            const detalheOpcao = opcao.detalhe
              ? traduzir(normalizarTextoPtBr(opcao.detalhe))
              : "";
            const botaoOpcao = (
              <button
                key={String(opcao.valor)}
                type="button"
                disabled={opcao.disabled}
                role={preferenciaPadrao ? undefined : "option"}
                aria-disabled={opcao.disabled || undefined}
                aria-selected={preferenciaPadrao ? undefined : String(opcao.valor ?? "") === valorNormalizado}
                aria-pressed={preferenciaPadrao ? String(opcao.valor ?? "") === valorNormalizado : undefined}
                className={`${styles.opcao} ${opcao.disabled ? styles.opcaoDesabilitada : ""} ${String(opcao.valor ?? "") === valorNormalizado ? styles.opcaoAtiva : ""}`}
                onClick={() => {
                  if (opcao.disabled) return;
                  onChange(String(opcao.valor ?? ""));
                  onChangeOpcao?.(opcao);
                  setAberto(false);
                }}
              >
                <span className={styles.opcaoConteudo}>
                  {opcao.icone ? (
                    <span className={styles.iconeOpcao} aria-hidden="true">{opcao.icone}</span>
                  ) : opcao.imagemUrl ? (
                    <img className={styles.thumbImagem} src={opcao.imagemUrl} alt="" aria-hidden="true" />
                  ) : opcao.avatarTexto ? (
                    <span
                      className={styles.thumbFallback}
                      style={avatarRedondo || avatarQuadrado ? estiloAvatarCadastro(opcao.avatarTexto) : undefined}
                      aria-hidden="true"
                    >
                      {textoAvatarSelect(opcao.avatarTexto)}
                    </span>
                  ) : opcao.cor ? (
                    <span
                      className={styles.bolinhaCor}
                      style={{ ["--opcao-cor" as string]: opcao.cor } as CSSProperties}
                      aria-hidden="true"
                    />
                  ) : null}
                  <span className={styles.opcaoTextos}>
                    <span className={styles.opcaoRotulo} data-i18n-db={opcaoEhDadoBanco ? "true" : undefined}>
                      {opcaoEhDadoBanco ? normalizarTextoPtBr(opcao.rotulo) : traduzir(normalizarTextoPtBr(opcao.rotulo))}
                    </span>
                    {detalheOpcao && <span className={styles.opcaoDetalhe}>{detalheOpcao}</span>}
                  </span>
                  {opcao.estoqueQuantidade !== undefined ? (
                    <span className={styles.opcaoEstoque}>
                      {Number(opcao.estoqueQuantidade || 0).toLocaleString("pt-BR")}
                    </span>
                  ) : null}
                </span>
              </button>
            );
            if (!preferenciaPadrao) return botaoOpcao;
            const marcado = String(opcao.valor ?? "") === preferenciaPadrao.valor;
            const descricaoPadrao = `${traduzir(marcado ? "Padrão para novos pedidos:" : "Definir como padrão para novos pedidos:")} ${normalizarTextoPtBr(opcao.rotulo)}`;
            // Botões irmãos: nunca aninha o rádio dentro do botão de seleção da opção.
            return <div key={String(opcao.valor)} className={styles.opcaoLinha}>
              {botaoOpcao}
              <button type="button" role="radio" aria-checked={marcado}
                disabled={opcao.disabled || preferenciaPadrao.salvando}
                className={`${styles.padraoRadio} tooltip`} data-tooltip={descricaoPadrao} aria-label={descricaoPadrao}
                onClick={(evento) => {
                  evento.stopPropagation();
                  if (!marcado) preferenciaPadrao.onSalvar(String(opcao.valor ?? ""));
                }} />
            </div>;
          })}

          {opcoesFiltradas.length === 0 && <div className={styles.vazio}>{traduzir("Nenhuma opção encontrada")}</div>}
        </div>
      </Popover>
    </div>
  );
}
